import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
type ScrollBacklightProps = {
  /** Glow tint. "caramel" for dark sections, "cream" for lighter ones. */
  tone?: 'caramel' | 'cream';
  className?: string;
};
/**
 * A soft pool of light that sits BEHIND an image and lights up as the image
 * scrolls through the viewport — brightest when centered, dim at the edges.
 * Purely decorative + aria-hidden.
 *
 * Usage: place inside a `relative` wrapper, as a sibling *before* the image,
 * so it renders behind it. The wrapper should NOT clip it (no overflow-hidden
 * on the immediate parent, or the glow won't spill behind the frame).
 */
export function ScrollBacklight({
  tone = 'caramel',
  className = ''
}: ScrollBacklightProps) {
  const ref = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  // Brightest as the element passes through the vertical center of the viewport.
  const opacity = useTransform(scrollYProgress, [0, 0.35, 0.5, 0.65, 1], [0, 0.5, 1, 0.5, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.82, 1.12, 0.82]);
  const gradient = tone === 'caramel' ? 'radial-gradient(ellipse at center, rgba(185,130,74,0.55), rgba(185,130,74,0.18) 45%, transparent 72%)' : 'radial-gradient(ellipse at center, rgba(247,240,232,0.5), rgba(247,240,232,0.16) 45%, transparent 72%)';
  return <motion.div ref={ref} aria-hidden className={`absolute -inset-8 lg:-inset-12 rounded-full pointer-events-none -z-10 ${className}`} style={{
    opacity,
    scale,
    background: gradient,
    filter: 'blur(50px)'
  }} data-id="element-336" data-mp="1873txmumsnmk" />;
}